import { Location } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { Injectable, OnDestroy } from '@angular/core'
import { CommandService } from '@helios/commands'
import { HttpAccountService, RootIdType } from '@helios/common-api'
import {
  AuxiliaryUpdateEvent,
  CommandsUpdateEvent,
  EventType,
  GlobalMessagingService,
  HeliosMessageType,
  SessionBasedEvent,
  SessionMessagingService,
  SessionStartEvent,
} from '@helios/messaging'
import {
  AccountCommandCount,
  addSession,
  clearCommands,
  clearSessionCwTxDataAvailableTime,
  clearSessionExtTxDataAvailableTime,
  clearSessionPositionDataAvailableTime,
  clearSessionStartTime,
  CreatedRequestDetails,
  inactivateSession,
  PeriodLength,
  periodLengthToDate,
  removeSession,
  RootState,
  selectActiveSession,
  selectActualReconDate,
  selectLegacyUserId,
  selectSelectedReconDate,
  selectSessions,
  SessionWithState,
  setActiveSession,
  setCommands,
  setFocusedAccountInfo,
  setFocusedAccounts,
  setIsCreatingSession,
  setIsSwitchingSession,
  setSessionCommands,
  setSessions,
  storeSessionStartTime,
  toggleSessionPin,
  updateSessionAuxiliaryInfo,
} from '@helios/state-management'
import { UserPreferencesService } from '@helios/user-preferences'
import { compareSets, getZeroUtcDate } from '@helios/utils'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { BehaviorSubject, EMPTY, of, Subject, throwError } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  mergeAll,
  mergeMap,
  switchMap,
  take,
  toArray,
} from 'rxjs/operators'
import { CloseSessionModalService } from '../../components/modals/close-session-modal/close-session-modal.service'
import { CreateNewSessionModalService } from '../../components/modals/create-new-session/create-new-session-modal.service'
import { UncommittedChangesAutoRefreshModalService } from '../../components/modals/uncommitted-changes-auto-refresh/uncommitted-changes-auto-refresh-modal.service'
import { UncommittedChangesDetectedModalService } from '../../components/modals/uncommitted-changes-detected/uncommitted-changes-detected-modal.service'
import { SessionAction } from '../../models/session-action.model'
import { HttpAccountsService } from '../http-accounts-service/http-accounts.service'
import { HttpSessionsService } from '../http-sessions-service/http-sessions.service'

export const MAX_ALLOWED_SESSIONS = 20
export const CANNOT_LOAD_AGG_ACCOUNT =
  'Loading an aggregate account is not supported at this time'
export const MAX_PINNED_SESSIONS = 5
export const MAX_PINNED_SESSIONS_EXCEEDED =
  'You have reached the maximum number of pinned sessions (5). Close a pinned session.'
export const SESSION_LIMIT_EXCEEDED =
  'Session limit is met. Please close one or more open session(s) to continue.'
export const SESSION_NOT_FOUND = 'Account does not exist or is inactive.'

@Injectable({
  providedIn: 'root',
})
export class SessionsService implements OnDestroy {
  activeSession?: SessionWithState = undefined
  sessions: SessionWithState[] = []
  /** @internal */
  currentUserId?: number
  /** @internal */
  disableSSEAddSession = false
  private readonly _destroyed$ = new Subject<void>()

  /** @internal */
  selectedReconDate?: Date
  defaultReconDate = new Date()
  defaultPeriodLength = periodLengthToDate(PeriodLength.Last100Days)

  isSessionsLoaded = new BehaviorSubject<boolean>(false)

  constructor(
    private readonly store: Store<RootState>,
    private readonly httpSessionsService: HttpSessionsService,
    private readonly commandsService: CommandService,
    private readonly toastService: ToastService,
    private readonly sessionMessagingService: SessionMessagingService,
    private readonly globalMessagingService: GlobalMessagingService,
    private readonly location: Location,
    private readonly createNewSessionModalService: CreateNewSessionModalService,
    private readonly httpAccountsService: HttpAccountsService,
    private readonly httpAccountService: HttpAccountService,
    private readonly uncommittedChangesDetectedModalService: UncommittedChangesDetectedModalService,
    private readonly uncommittedChangesAutoRefreshModalService: UncommittedChangesAutoRefreshModalService,
    private readonly closeSessionModalService: CloseSessionModalService,
  ) {
    this.init()
  }

  ngOnDestroy() {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  async init() {
    this.store
      .select(selectActiveSession)
      .pipe(distinctUntilChanged((a, b) => a?.id === b?.id))
      .subscribe(session => (this.activeSession = session))

    this.store
      .select(selectSessions)
      .subscribe(sessions => (this.sessions = sessions))

    this.globalMessagingService
      .on<unknown, CommandsUpdateEvent>(
        EventType.MESSAGE,
        HeliosMessageType.CommandsUpdate,
      )
      .subscribe(event => {
        if (!event.payload) return

        if (this.sessions.some(s => s.id === event.heliosSessionId)) {
          this.store.dispatch(
            setSessionCommands({
              sessionId: event.heliosSessionId ?? '',
              commands: event.payload as unknown as AccountCommandCount[],
            }),
          )
        }
      })

    this.globalMessagingService
      .on<unknown, AuxiliaryUpdateEvent>(
        EventType.MESSAGE,
        HeliosMessageType.AuxiliaryUpdate,
      )
      .subscribe(event => {
        if (!event.payload || !event.heliosSessionId) return

        if (this.sessions.some(s => s.id === event.heliosSessionId)) {
          this.store.dispatch(
            updateSessionAuxiliaryInfo({
              sessionId: event.heliosSessionId,
              auxiliaryInfo: event.payload,
            }),
          )
        }
      })

    this.globalMessagingService
      .on<CreatedRequestDetails[], SessionBasedEvent<CreatedRequestDetails[]>>(
        EventType.MESSAGE,
        HeliosMessageType.RestrictedRequestIdsUpdate,
      )
      .subscribe(event => {
        if (!event.heliosSessionId) return

        if (this.sessions.some(s => s.id === event.heliosSessionId)) {
          this.store.dispatch(
            updateSessionAuxiliaryInfo({
              sessionId: event.heliosSessionId,
              auxiliaryInfo: { REQUEST_IDS: event.payload ?? [] },
            }),
          )
        }
      })

    this.store
      .select(selectLegacyUserId)
      .subscribe(currentUserId => (this.currentUserId = currentUserId))

    this.store
      .select(selectActualReconDate)
      .pipe(take(1))
      .subscribe(reconDate => {
        if (reconDate) {
          this.defaultReconDate = reconDate
        }
      })

    this.store
      .select(selectSelectedReconDate)
      .pipe(take(1))
      .subscribe(selectedReconDate => {
        if (selectedReconDate) {
          this.selectedReconDate = selectedReconDate
        }
      })

    this.subscribeSessionStart()
  }

  /**
   * Closes the session and cleans up the redux store
   */
  async close(sessionId: string) {
    const activeSessionId = this.activeSession?.id
    const session = this.sessions.find(s => s.id === sessionId)

    // Check if session has needsReview flag in auxiliaryInfo
    if (session?.auxiliaryInfo?.NEED_REVIEW) {
      const confirmed = await this.closeSessionModalService.confirm(
        sessionId,
        true,
      )

      if (!confirmed) {
        return // User canceled the operation
      }
    }

    await this.httpSessionsService.deleteSession(sessionId).toPromise()
    localStorage.removeItem(sessionId)
    this.store.dispatch(removeSession({ sessionId: sessionId }))
    this.store.dispatch(inactivateSession({ sessionId: sessionId }))
    this.store.dispatch(clearSessionStartTime({ sessionId }))
    this.store.dispatch(clearSessionPositionDataAvailableTime({ sessionId }))
    this.store.dispatch(clearSessionExtTxDataAvailableTime({ sessionId }))
    this.store.dispatch(clearSessionCwTxDataAvailableTime({ sessionId }))

    if (activeSessionId === sessionId) {
      this.store.dispatch(clearCommands())
      this.store.dispatch(setFocusedAccounts({ accountIds: [] }))
      this.store.dispatch(setFocusedAccountInfo({ accountInfo: [] }))
      this.updateURL()
    }
  }

  async checkForUncommittedCommandsAndCloseSession(sessionId: string) {
    const sessions = await this.getSessions()
    const sessionToClose = sessions.find(x => x.id === sessionId)
    const sessionCommands = sessionToClose?.sumOfCommands ?? 0

    const needsReview = sessionToClose?.auxiliaryInfo?.NEED_REVIEW
    if (sessionCommands > 0 && !needsReview) {
      return this.resolveUncommittedChanges(sessionId, sessionCommands)
    } else return this.close(sessionId)
  }

  async checkForUncommittedCommandsAndCloseSessionOnAutoRefresh(
    sessionId: string,
  ) {
    const sessions = await this.getSessions()
    const sessionToClose = sessions.find(x => x.id === sessionId)
    const sessionCommands = sessionToClose?.sumOfCommands ?? 0

    if (sessionCommands > 0) {
      return this.resolveUncommittedChangesOnAutoRefresh(
        sessionId,
        sessionCommands,
      )
    } else return this.close(sessionId)
  }

  async resolveUncommittedChanges(sessionId: string, commandsCount: number) {
    const sessionToResolve = (await this.getSessions()).find(
      x => x.id == sessionId,
    )

    if (!sessionToResolve?.accountCommands) {
      return EMPTY.toPromise()
    }

    const uncommittedModal =
      await this.uncommittedChangesDetectedModalService.toggleUncommittedChangesModal(
        sessionToResolve?.loadedAccountIds,
        commandsCount,
      )

    if (!uncommittedModal) return EMPTY.toPromise()

    switch (uncommittedModal.action) {
      case SessionAction.DISCARD:
        return this.close(sessionId)
      case SessionAction.RUN_WITHOUT_ETLS:
        return this.runSession(sessionId, false)
      case SessionAction.RUN_WITH_ETLS:
        return this.runSession(sessionId, true)
      default:
        console.error(`Unsupported action: ${uncommittedModal.action} `)
        return EMPTY.toPromise()
    }
  }

  async resolveUncommittedChangesOnAutoRefresh(
    sessionId: string,
    commandsCount: number,
  ) {
    const sessionToResolve = (await this.getSessions()).find(
      x => x.id == sessionId,
    )

    if (!sessionToResolve?.accountCommands) {
      return EMPTY.toPromise()
    }

    const uncommittedModal =
      await this.uncommittedChangesAutoRefreshModalService.toggleUncommittedChangesModal(
        sessionToResolve?.loadedAccountIds,
        commandsCount,
      )

    if (!uncommittedModal) return EMPTY.toPromise()

    switch (uncommittedModal.action) {
      case SessionAction.DISCARD:
        return this.close(sessionId)
      case SessionAction.KEEP:
        return Promise.resolve(SessionAction.KEEP)
      case SessionAction.RUN_WITH_ETLS:
        return this.runSession(sessionId, true)
      default:
        console.error(`Unsupported action: ${uncommittedModal.action} `)
        return EMPTY.toPromise()
    }
  }

  runSession(sessionId: string, runEtls: boolean) {
    return this.commandsService
      .runAccountBySession(sessionId, runEtls)
      .pipe(
        take(1),
        switchMap(() => this.close(sessionId)),
      )
      .toPromise()
  }

  /**
   * Set session pin state
   */
  async updateSessionPinStatus(sessionId: string, isPinned: boolean) {
    const sessions = await this.getSessions()
    const sessionsPinned = sessions.filter(x => x.isPinned)

    if (sessionsPinned.length < MAX_PINNED_SESSIONS) {
      isPinned
        ? localStorage.setItem(sessionId, 'true')
        : localStorage.removeItem(sessionId)
      this.store.dispatch(toggleSessionPin({ sessionId, isPinned }))
    }
  }

  /**
   * Creates a new session from modal
   */
  async createFromModal(
    accountIds: number[],
    reconDate?: Date,
    customPeriodLength: boolean = false,
    isLIBAccount: boolean = false,
  ) {
    const response =
      await this.createNewSessionModalService.toggleCreateNewSessionModal(
        accountIds,
        reconDate,
        customPeriodLength,
        isLIBAccount,
      )
    if (!response) return
    const loadWindowStartDate = periodLengthToDate(
      response.periodLength,
      response.periodLengthNFromDate,
    )

    return this.create(
      response.accountIds,
      response.reconDate,
      loadWindowStartDate,
      response.overrideAutoclose,
      response.selectedBasis,
    )
  }

  /**
   * The only exposed create session api.
   *
   * This performs the workflow around creating a session.
   * Checks:
   *  1. Auto close
   *    a. Check all the loaded sessions
   *      i. If the session has no commands, close session
   *  2. Check session limit exceeded
   *  3. Does this account exist in another session?
   *    a. Does it have existing commands?
   *      true - rejoin the session
   *      false - refresh the session
   *    b. Activate session
   */
  async create(
    accountIds: number[],
    reconDate: Date,
    loadWindowStartDate: Date,
    overrideAutoclose: boolean,
    loadWithBasis?: string,
    loadWithDefaultBasis?: boolean,
  ): Promise<SessionWithState | undefined> {
    this.disableSSEAddSession = true
    // overrides the autoclose/rejoin session workflow
    // when user creates a session and selects 'create' it will create a new session even if accountID is found in an already created session
    if (overrideAutoclose) {
      const createdSession = await this.createAndSetSessionWithState(
        accountIds,
        reconDate,
        loadWindowStartDate,
        loadWithBasis,
        loadWithDefaultBasis,
      )
      await this.activateSession(createdSession.id)
      this.disableSSEAddSession = false
      return createdSession
    }
    await this.autoCloseSessions()
    const hasLimitExceeded = await this.isSessionLimitExceeded()

    if (hasLimitExceeded) return undefined

    const isAccountSupported = await this.isAccountTypeAllowed(accountIds)

    if (!isAccountSupported) {
      this.disableSSEAddSession = false
      return EMPTY.toPromise()
    }

    const sessionFound = await this.accountsFoundInSessions(accountIds)
    let rejoinSession = false

    if (
      (sessionFound?.isPinned && !overrideAutoclose) ||
      (sessionFound?.sumOfCommands && sessionFound.sumOfCommands >= 1)
    ) {
      rejoinSession = true
    }

    if (rejoinSession) {
      await this.activateSession(sessionFound.id)
      this.disableSSEAddSession = false
      return sessionFound
    } else {
      const createdSession = await this.createAndSetSessionWithState(
        accountIds,
        reconDate,
        loadWindowStartDate,
        loadWithBasis,
        loadWithDefaultBasis,
      )

      await this.activateSession(createdSession.id)
      this.disableSSEAddSession = false
      return createdSession
    }
  }

  /**
   * This method will make sure user doesn't have unsubmitted commands
   *    if it does, open modal to resolve them
   *    then it will close the session and open a new session with same accountIds
   *
   * @param session
   */
  async refreshSession(session: SessionWithState) {
    await this.checkForUncommittedCommandsAndCloseSession(session.id)
    const newSession = await this.create(
      session.loadedAccountIds,
      session.reconDate,
      session.auxiliaryInfo?.LOAD_WINDOW_START_DATE ??
        periodLengthToDate(PeriodLength.Last100Days),
      true,
      session.auxiliaryInfo?.BASIS_NAME,
    )
    if (newSession) {
      await this.activateSession(newSession.id)
    }
  }

  /**
   * This method will make sure user doesn't have unsubmitted commands
   *    if it does, open modal to resolve them
   *    Modal can return keep to signal to do nothing
   *    modal can run close and return on discard and refresh and this method then creates new session
   *    modal can run etl,run close and return on submit and refresh and this method then creates new session
   *    if no unsubmitted command we get close session back and this method then creates new session
   *
   * @param session
   * @param affectedLoadedAccountIds
   */
  async autoRefreshSessionOnJobComplete(
    session: SessionWithState,
    affectedLoadedAccountIds: number[],
  ) {
    // Check if session has pending review items
    const sessionObj = this.sessions.find(s => s.id === session.id)
    if (sessionObj?.auxiliaryInfo?.NEED_REVIEW) {
      this.toastService.error(
        'Cannot Auto Refresh session with pending approval items',
      )
      return
    }

    const refreshStatus =
      await this.checkForUncommittedCommandsAndCloseSessionOnAutoRefresh(
        session.id,
      )
    if (refreshStatus == SessionAction.KEEP) {
      return
    }

    const newSession = await this.create(
      session.loadedAccountIds,
      session.reconDate,
      session.auxiliaryInfo?.LOAD_WINDOW_START_DATE ??
        periodLengthToDate(PeriodLength.Last100Days),
      true,
      session.auxiliaryInfo?.BASIS_NAME,
    )
    if (newSession) {
      await this.activateSession(newSession.id)
      this.toastSessionAutoRefreshed(affectedLoadedAccountIds)
    }
  }

  /**
   * Check account type
   * This checks if the account type is an aggregate or sleeve aggregate.
   * If its an aggregate then show toast 'account type is not supported'
   * If it is a sleeve aggregate, ask user if they want to proceed, but warn them it is
   *    not fully supported
   *
   */
  async isAccountTypeAllowed(accountIds: number[]) {
    if (accountIds == null) return true

    const accountType = await this.httpAccountsService
      .getAccountType(accountIds)
      .toPromise()

    for (const type of accountType) {
      if (type === 'aggregate') {
        this.toastService.warning(CANNOT_LOAD_AGG_ACCOUNT)
        return false
      }
    }

    return true
  }

  /**
   * @internal
   * Loop through each session.
   *  Close session if:
   *    1. No commands
   *    2. Is pinned
   *    3. Close all sessions if Force Close Session Preference is ON.
   */
  async autoCloseSessions() {
    const sessions = await this.getSessions()
    const closableSessions = sessions.filter(
      x => x.sumOfCommands === 0 && !x.isPinned,
    )
    const sessionsWithCommands = sessions.filter(
      x => x.sumOfCommands !== 0 && !x.isPinned,
    )

    let response
    if (UserPreferencesService.getForceCloseSessionPreference()) {
      response = sessionsWithCommands.map(x => {
        if (x.sumOfCommands && !x.auxiliaryInfo?.NEED_REVIEW) {
          this.resolveUncommittedChanges(x.id, x.sumOfCommands)
        }
      })
    }
    response = closableSessions.map(x => this.close(x.id))
    await Promise.all(response)
  }

  /** @internal */
  async accountsFoundInSessions(accountIds: number[]) {
    const sessions = await this.getSessions()
    const session = sessions.find(s =>
      compareSets(new Set(s.loadedAccountIds), new Set(accountIds)),
    ) as SessionWithState

    return session
  }

  /**
   * @internal
   *
   * If the session limit is met, then show a toast
   */
  async isSessionLimitExceeded(): Promise<boolean> {
    const sessions = await this.getSessions()
    if (sessions.length >= MAX_ALLOWED_SESSIONS) {
      this.toastService.error(SESSION_LIMIT_EXCEEDED)
      return Promise.resolve(true)
    }
    return Promise.resolve(false)
  }

  /**
   * @internal
   *
   * 1. Find the session
   * 2. Set in redux and the focused accounts
   * 3. Populate commands
   */
  async activateSession(sessionId: string) {
    const sessions = await this.getSessions()
    const session = sessions.find(x => x.id === sessionId)
    if (!session) return
    this.store.dispatch(setActiveSession({ session: session }))
    this.store.dispatch(
      setFocusedAccounts({ accountIds: session.loadedAccountIds }),
    )
    if (session.loadedAccounts)
      this.store.dispatch(
        setFocusedAccountInfo({ accountInfo: session.loadedAccounts }),
      )

    this.updateURL(session.id)

    // We purposely do not await this
    this.populateCommands(session.id)
  }

  /**
   * @internal
   *
   * Get the commands from the service for sessionId and set in redux
   */
  async populateCommands(sessionId: string): Promise<void> {
    const commands = await this.commandsService
      .getCommands(sessionId)
      .toPromise()
    this.store.dispatch(setCommands({ commands }))
  }

  /**
   * @internal
   *
   * 1. Get the raw sessions from the services
   * 2. Set the session info for each session
   * 3. Set to store
   */
  async loadSessions(): Promise<SessionWithState[]> {
    const sessions = await this.httpSessionsService
      .loadSessions()
      .pipe(
        take(1),
        mergeAll(),
        mergeMap(session => this.setSessionInfo(session as SessionWithState)),
        toArray(),
      )
      .toPromise()

    this.store.dispatch(setSessions({ sessions: sessions }))
    this.isSessionsLoaded.next(true)

    return sessions
  }

  /** @internal */
  async getSessions() {
    const sessions = await this.store
      .select(selectSessions)
      .pipe(take(1))
      .toPromise()
    return sessions
  }

  /**
   * @internal
   * Create a new session:
   *  - Calls the create session api
   *  - Gets and sets the session info
   */
  async createAndSetSessionWithState(
    accountIds: number[],
    reconDate: Date,
    loadWindowStartDate: Date,
    loadWithBasis?: string,
    loadWithDefaultBasis?: boolean,
  ): Promise<SessionWithState> {
    const startTime = Date.now() // Capture the start time of the session creation
    this.store.dispatch(setIsCreatingSession({ isCreating: true }))
    this.store.dispatch(setIsSwitchingSession({ isSwitching: true }))
    this.toastNewSessionCreated(accountIds)
    let createdSession = (await this.httpSessionsService
      .createSession(
        accountIds,
        reconDate,
        loadWindowStartDate,
        loadWithBasis,
        loadWithDefaultBasis,
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 400) {
            this.toastService.error('Account does not exist or is inactive.')
          } else if (err.status === 409)
            this.toastService.error(SESSION_LIMIT_EXCEEDED)
          return throwError(err)
        }),
      )
      .toPromise()) as SessionWithState

    createdSession = await this.setSessionInfo(createdSession)

    // Store session start time and session id in redux
    this.store.dispatch(
      storeSessionStartTime({ sessionId: createdSession.id, startTime }),
    )

    this.store.dispatch(addSession({ session: createdSession }))
    this.store.dispatch(setIsCreatingSession({ isCreating: false }))
    this.store.dispatch(setIsSwitchingSession({ isSwitching: false }))
    return createdSession
  }

  /**
   * @internal
   * Set the non-core session data. This includes:
   *  - Users in the session
   *  - Number of commands in the session
   *  - Other auxillary info such as loaded window size
   */
  async setSessionInfo(originalSession: SessionWithState) {
    if (!originalSession) {
      return originalSession
    }

    const session = { ...originalSession }

    const getUsersPromise = this.httpSessionsService
      .getUsersInSession(session.id)
      .toPromise()
    const getAuxInfoPromise = this.httpSessionsService
      .getAuxillaryInfo(session.id)
      .toPromise()
    const getCommandsPromise = this.commandsService
      .getCommands(session.id)
      .toPromise()
    const getRequestIdsPromise = this.httpSessionsService
      .getRestrictedRequestIds(session.id)
      .pipe(catchError(() => of([])))
      .toPromise()

    const [users, auxInfo, commands, requestIds] = await Promise.all([
      getUsersPromise,
      getAuxInfoPromise,
      getCommandsPromise,
      getRequestIdsPromise,
    ])

    auxInfo.REQUEST_IDS = requestIds ?? []

    let isHplAccount = false
    let isEnfusionAccount = false
    let loadedForNonDefaultBasis = false
    if (originalSession.loadedAccountIds.length > 0) {
      // Run HPL and Enfusion checks in parallel
      const [isHplAccounts, ultimateParentClients] = await Promise.allSettled([
        this.httpSessionsService
          .getIsHplAccount(originalSession.loadedAccountIds[0])
          .toPromise(),
        this.httpAccountService
          .getAccountUltimateParentClients(
            originalSession.loadedAccountIds,
            RootIdType.account,
          )
          .toPromise(),
      ])

      // Process HPL result
      if (isHplAccounts.status === 'fulfilled') {
        isHplAccount =
          isHplAccounts.value?.[originalSession.loadedAccountIds[0]] ?? false
      } else {
        console.error('Failed to check HPL status:', isHplAccounts.reason)
        isHplAccount = false
      }

      // Process Enfusion result
      if (ultimateParentClients.status === 'fulfilled') {
        isEnfusionAccount = ultimateParentClients.value.some(
          client => client.id == '53938',
        )
      } else {
        console.error(
          'Failed to check Enfusion status:',
          ultimateParentClients.reason,
        )
        isEnfusionAccount = false
      }

      if (auxInfo.BASIS_ID) {
        const libDetails = await this.httpAccountsService
          .getLIBDetails(originalSession.loadedAccountIds[0])
          .toPromise()
        loadedForNonDefaultBasis =
          libDetails.basisDetails.DEFAULT_BASIS_ID != auxInfo.BASIS_ID
      }
    }

    const accountCommands: AccountCommandCount[] = []
    const accountToCommandCount = commands.reduce((accountToCount, command) => {
      const accountId = command.command.accountId
      const newCommandCount = (accountToCount.get(accountId) ?? 0) + 1
      accountToCount.set(accountId, newCommandCount)
      return accountToCount
    }, new Map<number, number>())
    accountToCommandCount.forEach((commandCount, accountId) => {
      accountCommands.push({
        accountId,
        commandCount,
      })
    })

    session.users = users
    session.auxiliaryInfo = auxInfo
    session.sumOfCommands = commands.length
    session.accountCommands = accountCommands
    session.isHplAccount = isHplAccount
    session.isEnfusionAccount = isEnfusionAccount
    session.loadedForNonDefaultBasis = loadedForNonDefaultBasis

    if (localStorage.getItem(session.id)) {
      await this.updateSessionPinStatus(session.id, true)
      session.isPinned = true
    } else {
      await this.updateSessionPinStatus(session.id, false)
      session.isPinned = false
    }

    return session
  }

  /**
   * @internal
   * Handles the toast notification messages
   */
  toastNewSessionCreated(accountIds: number[]) {
    const toastHeader = `Creating New Session`
    const toastBody = `A new session for account ${accountIds.join(
      ', ',
    )} is being created and will be available in the Sessions panel.`
    this.toastService.success(toastBody, undefined, toastHeader)
  }

  /* @internal
   * Handles the toast notification messages
   */
  toastSessionAutoRefreshed(affectedLoadedAccountIds: number[]) {
    const toastHeader = `Session Refreshed Automatically`
    const toastBody = `The following account(s) ran successfully: ${affectedLoadedAccountIds.join(
      ', ',
    )}.
Data in your session has been refreshed.`
    this.toastService.success(toastBody, undefined, toastHeader)
  }

  /**
   * @internal
   *
   * Perform and url push state
   */
  updateURL(sessionId?: string) {
    const queryParams = this.location.path().split('?')[1]
    if (!sessionId) {
      // Clear the url
      this.location.replaceState('/' + (queryParams ? '?' + queryParams : ''))
    } else {
      // Populate the url
      this.location.replaceState(
        `/session/${sessionId}` + (queryParams ? '?' + queryParams : ''),
      )
    }
  }

  /** @internal */
  subscribeSessionStart() {
    this.sessionMessagingService
      .on<unknown, SessionStartEvent>(
        EventType.MESSAGE,
        HeliosMessageType.SessionStart,
      )
      .subscribe(async event => {
        // Ignore any non-current user sessions
        if (
          (this.currentUserId != null &&
            event.payload.creationUserId !== this.currentUserId) ||
          this.disableSSEAddSession
        ) {
          return
        }
        const sessionWithState = await this.setSessionInfo(event.payload)
        this.store.dispatch(addSession({ session: sessionWithState }))
      })
  }

  async copySessionToClipboard(sessionId: string, showToast = false) {
    const sessions = await this.getSessions()
    const session = sessions.find(x => x.id === sessionId)
    if (!session || !session.auxiliaryInfo?.LOAD_WINDOW_START_DATE) return

    const startDate = getZeroUtcDate(
      session.auxiliaryInfo.LOAD_WINDOW_START_DATE + '',
    )
    const reconDate = getZeroUtcDate(session.reconDate + '')

    const accountIds = session?.loadedAccountIds?.join(',')
    const basisName = session.auxiliaryInfo?.BASIS_NAME
    let url = `${
      window.location.origin
    }/helios?createFromAccountIds=${accountIds}&reconDate=${reconDate}&loadWindowStartDate=${encodeURIComponent(
      startDate,
    )}`
    if (basisName) {
      url = url.concat(`&basisName=${basisName}`)
    }
    window.navigator.clipboard.writeText(url)

    if (showToast) {
      this.toastService.success('Copied to clipboard')
    }
  }
}
